// Petit utilitaire pour imprimer une fiche (salarié, client, site, dossier
// disciplinaire...) sans dépendre du backend : on ouvre une fenêtre dédiée
// avec un HTML minimal et on déclenche l'impression du navigateur.
export function printHtml(title, bodyHtml) {
  const w = window.open("", "_blank", "width=850,height=1000");
  if (!w) {
    // eslint-disable-next-line no-alert
    alert("Le navigateur a bloqué l'ouverture de la fenêtre d'impression. Autorisez les pop-ups pour ce site.");
    return;
  }
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: Arial, Helvetica, sans-serif; color: #111827; padding: 32px; }
      h1 { font-size: 20px; margin: 0 0 2px; }
      h2 { font-size: 13px; text-transform: uppercase; letter-spacing: .08em; color: #6b7280; margin: 24px 0 8px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
      .subtitle { font-size: 13px; color: #6b7280; margin-bottom: 20px; }
      .row { display: flex; padding: 5px 0; border-bottom: 1px solid #f3f4f6; font-size: 13px; }
      .row .label { width: 220px; color: #6b7280; text-transform: uppercase; font-size: 11px; letter-spacing: .05em; padding-top: 2px; }
      .row .value { flex: 1; }
      table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 6px; }
      th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
      th { background: #f9fafb; font-size: 11px; text-transform: uppercase; color: #6b7280; }
      @media print { body { padding: 0; } }
    </style></head><body>${bodyHtml}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 350);
}

// Formate une date ISO (YYYY-MM-DD, éventuellement avec heure) au format
// français JJ/MM/AAAA — utilisé partout où une date est affichée à l'écran
// ou injectée dans un document imprimé.
export function dateFR(v) {
  if (!v) return null;
  const s = String(v).slice(0, 10);
  const parts = s.split("-");
  if (parts.length !== 3) return s;
  const [y, m, d] = parts;
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
}

export function row(label, value, isDate = false) {
  const display = isDate ? dateFR(value) : value;
  return `<div class="row"><div class="label">${label}</div><div class="value">${display ?? "—"}</div></div>`;
}

/**
 * Ouvre un document HTML complet (déjà autonome : <!doctype>, <head>, <body>…,
 * par ex. le content_html d'un document RH ou d'un contrat) dans un nouvel
 * onglet. Sert à la fois :
 *  - à l'aperçu avant téléchargement/impression (autoprint = false) ;
 *  - de solution de repli pour « télécharger en PDF » quand la génération
 *    PDF côté serveur est indisponible (autoprint = true déclenche la
 *    boîte d'impression du navigateur, où l'utilisateur choisit
 *    « Enregistrer au format PDF »).
 * Fonctionne sans dépendre d'un Chromium serveur, donc toujours disponible
 * même sur un hébergement mutualisé qui ne permet pas Puppeteer.
 */
export function openHtmlDocument(fullHtml, { autoprint = false } = {}) {
  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) {
    // eslint-disable-next-line no-alert
    alert("Le navigateur a bloqué l'ouverture de la fenêtre. Autorisez les pop-ups pour ce site.");
    return null;
  }
  w.document.write(fullHtml);
  w.document.close();
  w.focus();
  if (autoprint) setTimeout(() => { try { w.print(); } catch { /* ignore */ } }, 350);
  return w;
}

/**
 * Prépare un document HTML complet (content_html) pour une conversion PDF
 * côté navigateur : au lieu de le rendre dans un <iframe> caché (ancienne
 * méthode), on l'injecte directement dans la page courante, dans un
 * conteneur cadré. html2canvas capture alors exactement le même moteur de
 * rendu/CSS que l'aperçu affiché à l'écran — l'iframe posait problème car
 * html2canvas ne retrouvait pas toujours de façon fiable les <style> d'un
 * document étranger (cross-document), ce qui produisait un PDF sans aucune
 * mise en forme (texte brut, sans styles). Le <style> du document est copié
 * dans la page courante, avec son sélecteur "body" ré-écrit vers la classe
 * du conteneur (".pdf-render-root") pour ne pas affecter le reste de l'appli.
 */
async function renderHtmlForCapture(fullHtml) {
  const parsed = new DOMParser().parseFromString(fullHtml, "text/html");
  const styleText = Array.from(parsed.querySelectorAll("style")).map((s) => s.textContent).join("\n");
  // Ré-écrit le sélecteur "body" (utilisé dans tous les modèles de document :
  // police, marges, couleur…) vers la classe du conteneur, pour qu'il
  // s'applique bien une fois injecté hors de son <body> d'origine.
  const scopedStyle = styleText.replace(/\bbody\b/g, ".pdf-render-root");
  const bodyHtml = parsed.body ? parsed.body.innerHTML : fullHtml;

  // Certains documents (ex. "Contrat de nettoyage annuel — bureaux") utilisent
  // des marges NÉGATIVES sur une page de couverture/clôture plein cadre, pour
  // annuler le padding horizontal du <body> (1.8cm, qui recrée la marge de
  // page désormais gérée côté serveur via `padding` plutôt que `@page margin`
  // — voir le commentaire dans clientTemplates.js) et faire déborder une
  // photo/bandeau jusqu'au bord de la feuille. Dans CE contexte de capture,
  // le document est injecté dans un conteneur de 794px qui PORTE ce même
  // padding (le <style> du document, y compris la règle `body{padding:...}`,
  // est copié tel quel — voir plus haut). Il faut donc appliquer la MÊME
  // marge négative (-1.8cm, PAS zéro) pour annuler ce padding et obtenir un
  // vrai bord-à-bord, exactement comme dans le PDF généré côté serveur.
  // (Avant la restructuration du CSS en marge de page nulle + padding du
  // <body>, le gutter venait d'une marge @page que ce conteneur de capture
  // n'avait jamais — d'où l'ancienne neutralisation à zéro ici, devenue
  // incorrecte depuis que le gutter vient du padding du <body>.)
  const captureOverrides = `
    body::before{margin-left:-1.8cm!important;margin-right:-1.8cm!important}
    .satisfaction-banner{margin-left:-1.8cm!important;margin-right:-1.8cm!important}
    .cover-page{margin-left:-1.8cm!important;margin-right:-1.8cm!important}
    .closing-page{margin-left:-1.8cm!important;margin-right:-1.8cm!important}
  `;

  const styleEl = document.createElement("style");
  styleEl.setAttribute("data-pdf-render", "1");
  styleEl.textContent = `${scopedStyle}\n${captureOverrides}`;
  document.head.appendChild(styleEl);

  // IMPORTANT (constaté empiriquement, avec un vrai Chromium, sur le contrat
  // "bureaux" — 15 pages, ~9000px de haut) : donner au conteneur capturé
  // lui-même un `position` autre que `static` (que ce soit `fixed` OU
  // `absolute`, avec ou sans décalage hors écran) fait que html2canvas
  // calcule une hauteur de "0" pour cet élément, produisant un PDF
  // totalement vide — y compris pour `position: absolute; left: -10000px`,
  // qui avait pourtant été adopté précédemment comme corrigeant ce problème
  // (ça fonctionne pour de petits documents, mais pas pour un document aussi
  // long que celui-ci). Solution fiable : le conteneur capturé reste en
  // position NORMALE (`static`), et c'est un conteneur PARENT (jamais ciblé
  // par html2canvas) qui est réduit à une taille nulle avec overflow:hidden
  // pour le rendre invisible à l'écran — html2canvas mesure alors
  // correctement la hauteur réelle du contenu.
  const hiddenWrapper = document.createElement("div");
  hiddenWrapper.setAttribute("data-pdf-render-wrapper", "1");
  hiddenWrapper.style.position = "fixed";
  hiddenWrapper.style.top = "0";
  hiddenWrapper.style.left = "0";
  hiddenWrapper.style.width = "0";
  hiddenWrapper.style.height = "0";
  hiddenWrapper.style.overflow = "hidden";
  hiddenWrapper.style.zIndex = "-1";
  document.body.appendChild(hiddenWrapper);

  const container = document.createElement("div");
  container.className = "pdf-render-root";
  container.style.width = "794px"; // ~ largeur A4 à 96dpi
  container.style.background = "#ffffff";
  container.innerHTML = bodyHtml;
  hiddenWrapper.appendChild(container);

  // Laisse le temps aux images (logo, tampon, photos) de charger avant la capture.
  const images = Array.from(container.querySelectorAll("img"));
  await Promise.all(images.map((img) => (img.complete ? Promise.resolve() : new Promise((res) => {
    img.onload = res; img.onerror = res;
  }))));
  await new Promise((resolve) => setTimeout(resolve, 50));

  return { container, styleEl, hiddenWrapper };
}

function cleanupCapture({ hiddenWrapper, styleEl }) {
  hiddenWrapper.remove();
  styleEl.remove();
}

const HTML2PDF_OPTS = {
  margin: 0,
  image: { type: "jpeg", quality: 0.98 },
  html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
  jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
  // "avoid-all" : empêche TOUT élément HTML (paragraphe, ligne de tableau,
  // titre, bloc…) tenant sur moins d'une page d'être coupé en deux entre deux
  // pages — html2pdf.js pousse alors automatiquement l'élément entier sur la
  // page suivante plutôt que de le découper. Avant ce changement, seuls les
  // éléments explicitement marqués "page-break-inside: avoid" en CSS en
  // bénéficiaient ("css" mode) ; selon la police effectivement chargée dans
  // le navigateur de l'utilisateur (qui peut différer légèrement de celle du
  // test), la hauteur exacte de certains blocs pouvait changer suffisamment
  // pour tomber pile sur une limite de page et se retrouver coupés en deux
  // (un paragraphe, une ligne de tableau…). "avoid-all" rend cette protection
  // systématique, pour tous les blocs, sans dépendre d'un marquage CSS au cas
  // par cas.
  pagebreak: { mode: ["css", "avoid-all", "legacy"] },
};

/**
 * Télécharge un document HTML complet (content_html d'un contrat/avenant/
 * document RH) directement en PDF, généré DANS LE NAVIGATEUR (html2pdf.js —
 * html2canvas + jsPDF), sans dépendre d'un Chromium côté serveur. C'est la
 * méthode de téléchargement PDF principale de l'application : certains
 * hébergements mutualisés (type Hostinger) ne permettent pas de lancer un
 * navigateur headless côté serveur, donc on ne s'appuie plus dessus du tout.
 */
export async function downloadHtmlAsPdf(fullHtml, filename) {
  const html2pdf = (await import("html2pdf.js")).default;
  const rendered = await renderHtmlForCapture(fullHtml);
  try {
    await html2pdf().set({ ...HTML2PDF_OPTS, filename: filename || "document.pdf" }).from(rendered.container).save();
  } finally {
    cleanupCapture(rendered);
  }
}

/**
 * Ouvre un document HTML directement en PDF (html2pdf.js) dans un nouvel onglet,
 * pour que ce soit le lecteur PDF du navigateur (ou Adobe si configuré comme
 * lecteur par défaut) qui l'affiche, plutôt qu'un aperçu HTML brut à imprimer
 * manuellement. Repli sur openHtmlDocument si la génération PDF échoue.
 */
export async function openHtmlAsPdf(fullHtml, filename) {
  let rendered = null;
  try {
    const html2pdf = (await import("html2pdf.js")).default;
    rendered = await renderHtmlForCapture(fullHtml);
    const blobUrl = await html2pdf()
      .set({ ...HTML2PDF_OPTS, filename: filename || "document.pdf" })
      .from(rendered.container)
      .outputPdf("bloburl");
    window.open(blobUrl, "_blank");
  } catch (err) {
    openHtmlDocument(fullHtml, { autoprint: false });
  } finally {
    if (rendered) cleanupCapture(rendered);
  }
}
